import { useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PageTitle from "./PageTitle";
import LotteryDatePicker from "./LotteryDatePicker";
import LotterySearchForm from "./LotterySearchForm";
import ProvinceAccordion from "./ProvinceAccordion";
import LotteryResultTable from "./LotteryResultTable";
import PrizeRow from "./PrizeRow";
import useProvinces from "./useProvinces";
import useLotteryResults from "./useLotteryResults";
import useXosoLive, { selectLiveResults } from "./useXosoLive";
import { LOTTERY_TYPE_TITLES, REGION_META, resolveLotteryRegions } from "./constants";
import "./XoSoCompound.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

function XoSoCompound({
  title,
  showTitle = false,
  defaultDate,
  config,
  category,
  lotteryType,
}) {
  const regions = useMemo(
    () => resolveLotteryRegions(category ?? lotteryType ?? config?.lotteryType),
    [category, lotteryType, config?.lotteryType]
  );
  const heading = title || LOTTERY_TYPE_TITLES[regions.length === 1 ? regions[0] : "all"];
  const today = dayjs().tz(TZ).format("YYYY-MM-DD");
  const [date, setDate] = useState(defaultDate || today);
  const [provinceFilter, setProvinceFilter] = useState(null);
  const rootRef = useRef(null);
  const { byRegion } = useProvinces();
  const { results, loading } = useLotteryResults(date, regions);
  const isToday = date === today;
  const liveMB = useXosoLive("MB", isToday && regions.includes("MB"));
  const liveMN = useXosoLive("MN", isToday && regions.includes("MN"));
  const liveMT = useXosoLive("MT", isToday && regions.includes("MT"));
  const liveByRegion = { MB: liveMB, MN: liveMN, MT: liveMT };

  const merged = useMemo(
    () => ({
      MB: selectLiveResults(results.MB, liveMB),
      MN: selectLiveResults(results.MN, liveMN),
      MT: selectLiveResults(results.MT, liveMT),
    }),
    [results, liveMB, liveMN, liveMT]
  );

  const filtered = useMemo(() => {
    if (!provinceFilter) return merged;
    const { code, region } = provinceFilter;
    return {
      ...merged,
      [region]: (merged[region] || []).filter((item) => item.province?.code === code),
    };
  }, [merged, provinceFilter]);

  const scrollToRegion = (region) => {
    const el = rootRef.current?.querySelector(`#kqxs-${region}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDateChange = (next) => {
    setDate(next);
    setProvinceFilter(null);
  };

  const handleSearchMien = ({ region, date: nextDate }) => {
    setDate(nextDate);
    setProvinceFilter(null);
    setTimeout(() => scrollToRegion(region), 50);
  };

  const handleSearchTinh = ({ province, region, date: nextDate, name }) => {
    setDate(nextDate);
    setProvinceFilter({ code: province, region, name });
    setTimeout(() => scrollToRegion(region), 50);
  };

  const handleProvinceSelect = (province) => {
    setProvinceFilter({
      code: province.code,
      region: province.region,
      name: province.name,
    });
    scrollToRegion(province.region);
  };

  return (
    <div className="xoso-compound" ref={rootRef}>
      {showTitle ? <PageTitle>{heading}</PageTitle> : null}

      <div className="xoso-compound__layout">
        <aside className="xoso-compound__sidebar">
          <LotteryDatePicker value={date} onChange={handleDateChange} maxDate={today} />
          <LotterySearchForm
            date={date}
            provincesByRegion={byRegion}
            regions={regions}
            onSearchMien={handleSearchMien}
            onSearchTinh={handleSearchTinh}
          />
          <ProvinceAccordion
            provincesByRegion={byRegion}
            regions={regions}
            activeCode={provinceFilter?.code}
            onSelect={handleProvinceSelect}
          />
        </aside>

        <div className="xoso-compound__main">
          {provinceFilter ? (
            <div className="xoso-filter-chip">
              Đang lọc: {REGION_META[provinceFilter.region].label} — {provinceFilter.name}
              <button type="button" onClick={() => setProvinceFilter(null)} aria-label="Bỏ lọc">
                ×
              </button>
            </div>
          ) : null}

          {regions.includes("MB") ? (
            <LotteryResultTable
              region="MB"
              results={filtered.MB}
              date={date}
              loading={loading && !liveByRegion.MB.drawing && !liveByRegion.MB.complete}
              liveDrawing={liveByRegion.MB.drawing && Boolean(liveByRegion.MB.results?.length)}
            />
          ) : null}
          {regions.includes("MN") ? (
            <LotteryResultTable
              region="MN"
              results={filtered.MN}
              date={date}
              loading={loading && !liveByRegion.MN.drawing && !liveByRegion.MN.complete}
              liveDrawing={liveByRegion.MN.drawing && Boolean(liveByRegion.MN.results?.length)}
              titleOverride={
                provinceFilter?.region === "MN" ? provinceFilter.name : REGION_META.MN.label
              }
            />
          ) : null}
          {regions.includes("MT") ? (
            <LotteryResultTable
              region="MT"
              results={filtered.MT}
              date={date}
              loading={loading && !liveByRegion.MT.drawing && !liveByRegion.MT.complete}
              liveDrawing={liveByRegion.MT.drawing && Boolean(liveByRegion.MT.results?.length)}
              titleOverride={
                provinceFilter?.region === "MT" ? provinceFilter.name : REGION_META.MT.label
              }
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

XoSoCompound.PageTitle = PageTitle;
XoSoCompound.DatePicker = LotteryDatePicker;
XoSoCompound.SearchForm = LotterySearchForm;
XoSoCompound.ProvinceAccordion = ProvinceAccordion;
XoSoCompound.ResultTable = LotteryResultTable;
XoSoCompound.PrizeRow = PrizeRow;

export {
  PageTitle,
  LotteryDatePicker,
  LotterySearchForm,
  ProvinceAccordion,
  LotteryResultTable,
  PrizeRow,
};

export default XoSoCompound;
